<template>
  <div>
    <NuxtLinkLocale :to="`/event/${data?.fields?.path || ''}`">
      <span v-if="data?.fields?.title" :class="$style.title">
        {{ data?.fields?.title }}
      </span>
      <span :class="$style.date">{{ computedDates }}</span>
    </NuxtLinkLocale>
    <div>
      <NuxtLinkLocale
        v-if="data?.fields?.eventLink"
        :to="data?.fields?.eventLink"
        target="_blank"
        :class="$style.link"
        @click.stop="handleTitleClick"
      >
        <i :class="$style.linkIcon">
          <IconLink />
        </i>
        {{ localizedStrings['eventlink'] || 'EventLink' }}
        <i :class="$style.externalIcon"><IconExternalLink /></i>
      </NuxtLinkLocale>
    </div>

    <AddToCalendarButton
      :class="$style.link"
      :data="data"
      @add-to-calendar="handleCalendarClick"
    >
      <i :class="$style.linkIcon">
        <IconPlus />
      </i>
      {{ localizedStrings['add-to-calendar'] || 'Add to Calendar' }}
    </AddToCalendarButton>
  </div>
</template>

<script setup lang="ts">
import type { TypeEventWithoutUnresolvableLinksResponse } from '~/types/contentful'

const { locale } = useI18n()
const { localizedStrings } = useLocalizedStrings()

const props = defineProps<{
  data: TypeEventWithoutUnresolvableLinksResponse
}>()

const computedDates = computed(() => {
  const startDate = props.data?.fields?.startDatetime || ''
  const startDateFormatted = formatLocalizedDate(startDate, 'PP', locale?.value)
  const endDate = props.data?.fields?.endDatetime || ''
  const endDateFormatted = formatLocalizedDate(endDate, 'PP', locale?.value)
  return `${startDateFormatted} - ${endDateFormatted}`
})

const handleTitleClick = () => {
  pushToGaDataLayer({
    event: 'ga4.trackEvent',
    eventName: 'event_eventlink_click',
    eventParams: {
      event_name: props.data?.fields?.entryTitle,
    },
  })
}

const handleCalendarClick = () => {
  pushToGaDataLayer({
    event: 'ga4.trackEvent',
    eventName: 'event_add_to_calendar',
    eventParams: {
      event_name: props.data?.fields?.entryTitle,
    },
  })
}
</script>

<style lang="scss" module>
.title {
  font-weight: bold;
  display: block;
  margin-bottom: 0.25em;
}

.date {
  font-size: to-rem(16px);
  font-style: italic;
  font-weight: normal;
  text-transform: uppercase;
}

.link {
  background: none;
  border: none;

  font-size: 14px;
  font-weight: bold;

  padding: 0;
  margin: 1em 0 0;
  display: flex;
  align-items: center;
  color: var(--purple-light) !important;

  button {
    padding: 0;
  }

  &:hover {
    color: var(--purple) !important;
  }
}

.linkIcon {
  background: var(--purple-light);
  border-radius: 50%;
  color: var(--white);
  width: 21px;
  height: 21px;
  display: grid;
  place-content: center;
  padding: 4px;
  margin-right: 0.5em;

  .link:hover & {
    background: var(--purple);
  }

  svg {
    width: 100%;
    height: 100%;
  }
}

.externalIcon {
  width: 21px;
  height: 21px;
  padding: 3px;
  display: grid;
  place-content: center;

  svg {
    width: 100%;
    height: 100%;
  }
}
</style>
